import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { User, Mail, Phone } from "lucide-react";
import { useBooking } from "../../context/BookingContext";
import Button from "../common/Button";
import Input from "../common/Input";
import AddressSearch from "../common/AddressSearch";
import { contactFormSchema } from "../../utils/validation";
import { updateSessionData, getSessionData } from "../../utils/cookies";
import { analytics } from "../../utils/analytics";
import { databaseService } from "../../services/databaseService";
import { userJourneyService } from "../../services/userJourneyService";
import { formWebhookService } from "../../services/formWebhookService";
import { useUTMTracking } from "../../hooks/useUTMTracking";
import { Customer } from "../../types/user";
import { BOOKING_OPTIONS } from "../../utils/constants";
import { PerformanceProfiler } from "../../utils/performanceProfiler";
import { settingsService } from "../../services/settingsService";

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  bookingOption: string;
}

const ContactForm: React.FC = () => {
  PerformanceProfiler.mark("contact-form-start");

  const navigate = useNavigate();
  const { setCustomer, setCurrentStep } = useBooking();
  const [loading, setLoading] = useState(false);

  // Address validation state
  const [isAddressSelected, setIsAddressSelected] = useState(false);
  const [addressValidationError, setAddressValidationError] = useState<
    string | null
  >(null);
  const [hasRestoredValidSession, setHasRestoredValidSession] = useState(false);
  const [isRestoringSession, setIsRestoringSession] = useState(false);

  // Booking options state (filtered based on settings)
  const [availableBookingOptions, setAvailableBookingOptions] = useState(BOOKING_OPTIONS);
  const [bookingOptionsLoaded, setBookingOptionsLoaded] = useState(false);

  PerformanceProfiler.mark("utm-tracking-start");
  const { utmData, ga4ClientId } = useUTMTracking();
  PerformanceProfiler.measure("utm-tracking-init", "utm-tracking-start");

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: yupResolver(contactFormSchema),
    defaultValues: {
      bookingOption: "estimate",
    },
  });

  // Fetch booking options settings
  useEffect(() => {
    const loadBookingSettings = async () => {
      try {
        const estimateEnabled = await settingsService.isEstimateOptionEnabled();
        
        if (!estimateEnabled) {
          // Filter out estimate option, only show consultation
          const filteredOptions = BOOKING_OPTIONS.filter(opt => opt.id !== 'estimate');
          setAvailableBookingOptions(filteredOptions);
          // Set default to consultation when estimate is disabled
          setValue("bookingOption", "consultation");
        } else {
          setAvailableBookingOptions(BOOKING_OPTIONS);
        }
      } catch (error) {
        console.warn('Failed to load booking settings, using defaults:', error);
        setAvailableBookingOptions(BOOKING_OPTIONS);
      } finally {
        setBookingOptionsLoaded(true);
      }
    };

    loadBookingSettings();
  }, [setValue]);

  // Load saved data from session
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    PerformanceProfiler.mark("form-session-load-start");

    const sessionData = getSessionData();

    // Load form data (separate from customer data)
    if (sessionData?.formData) {
      const { firstName, lastName, email, phone, address, bookingOption } =
        sessionData.formData;
      setValue("firstName", firstName || "");
      setValue("lastName", lastName || "");
      setValue("email", email || "");
      setValue("phone", phone || "");
      setValue("address", address || "");
      setValue("bookingOption", bookingOption || "estimate");

      // Restore address validation state if address was previously selected from Google Places
      if (address && sessionData.customerData?.placeId) {
        console.log("🔧 Setting up valid session restoration:", {
          address,
          placeId: sessionData.customerData.placeId,
          beforeState: {
            isAddressSelected,
            addressValidationError,
            hasRestoredValidSession,
          },
        });

        setIsRestoringSession(true);
        setIsAddressSelected(true);
        setAddressValidationError(null);
        setHasRestoredValidSession(true);
        console.log("✅ Restored valid address from session:", address);

        setTimeout(() => {
          setIsRestoringSession(false);
          console.log("🔧 Session restoration complete, final state:", {
            isRestoringSession: false,
            isAddressSelected: true,
            hasRestoredValidSession: true,
          });
        }, 100);
      } else if (address && !sessionData.customerData?.placeId) {
        // Address exists but no placeId - check if it's a valid VIC address
        const isVicAddress =
          address.includes("VIC") || address.includes("Victoria");

        if (isVicAddress) {
          // Trust previously saved VIC addresses even without placeId
          setIsRestoringSession(true);
          setIsAddressSelected(true);
          setAddressValidationError(null);
          setHasRestoredValidSession(true);
          console.log(
            "✅ Restored valid VIC address (legacy session):",
            address
          );
          setTimeout(() => setIsRestoringSession(false), 100);
        } else {
          // Non-VIC address without placeId - treat as invalid
          setIsRestoringSession(true);
          setIsAddressSelected(false);
          setAddressValidationError(null); // Don't show error immediately on page load
          setHasRestoredValidSession(false);
          console.log("⚠️ Address restored but no placeId found:", address);
          setTimeout(() => setIsRestoringSession(false), 100);
        }
      }

      // Log for debugging in development
      if (process.env.NODE_ENV === "development") {
        console.log(
          "🔄 Restored form data from session:",
          sessionData.formData
        );
      }
    } else if (sessionData?.customerData) {
      // Fallback to customer data if form data doesn't exist
      const { firstName, lastName, email, phone, address } =
        sessionData.customerData;
      setValue("firstName", firstName || "");
      setValue("lastName", lastName || "");
      setValue("email", email || "");
      setValue("phone", phone || "");
      setValue("address", address || "");

      // Restore address validation state if address was previously selected from Google Places
      if (address && sessionData.customerData?.placeId) {
        setIsAddressSelected(true);
        setAddressValidationError(null);
        console.log("✅ Restored valid address from customerData:", address);
      } else if (address && !sessionData.customerData?.placeId) {
        // Address exists but no placeId - handle gracefully
        setIsAddressSelected(false);
        setAddressValidationError(null); // Don't show error immediately on page load
        console.log(
          "⚠️ Address restored from customerData but no placeId found:",
          address
        );
      }
    }

    analytics.trackPageView("contact_form");

    // Record user journey step
    userJourneyService.recordStep("contact_form_viewed", {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      referrer: document.referrer || "direct",
    });

    PerformanceProfiler.measure("form-session-load", "form-session-load-start");
    PerformanceProfiler.measure(
      "total-contact-form-init",
      "contact-form-start"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue]);

  // Save form data to session on changes (separate from customer data)
  const watchedFields = watch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Skip auto-save on initial load to prevent unnecessary saves
    if (
      Object.values(watchedFields).every(
        (value) => !value || value === "estimate"
      )
    ) {
      return;
    }

    // Debounce the updates to avoid excessive calls
    const timeoutId = setTimeout(() => {
      updateSessionData({
        formData: watchedFields,
        currentStep: "contact",
      });
    }, 500); // Increased debounce to 500ms for better UX

    return () => clearTimeout(timeoutId);
  }, [watchedFields]);

  const onSubmit = async (data: ContactFormData) => {
    setLoading(true);
    analytics.trackFormStart("contact_form");

    try {
      // Check if address is valid either from selection or session restoration
      const currentSessionData = getSessionData();
      const hasValidSessionAddress =
        currentSessionData?.customerData?.placeId &&
        currentSessionData?.customerData?.address === data.address;

      // Check if it's a trusted VIC address (even without placeId)
      const isTrustedVicAddress =
        data.address &&
        (data.address.includes("VIC") || data.address.includes("Victoria")) &&
        currentSessionData?.customerData?.address === data.address;

      // Validate that address was properly selected from Google Places OR has valid session data OR is trusted VIC address
      if (
        !isAddressSelected &&
        !hasValidSessionAddress &&
        !isTrustedVicAddress
      ) {
        console.log("❌ Address validation failed:", {
          isAddressSelected,
          hasValidSessionAddress,
          isTrustedVicAddress,
          addressValue: data.address,
          sessionPlaceId: currentSessionData?.customerData?.placeId,
        });

        setAddressValidationError(
          "Please select a valid address from the suggestions"
        );
        setLoading(false);
        analytics.trackError(
          "address_validation_failed",
          "Address not selected from suggestions",
          {
            addressValue: data.address,
            source: "contact_form",
            isAddressSelected,
            hasValidSessionAddress,
            isTrustedVicAddress,
          }
        );
        return;
      }

      console.log("✅ Address validation passed:", {
        isAddressSelected,
        hasValidSessionAddress,
        isTrustedVicAddress,
        addressValue: data.address,
      });

      // Clear any previous address validation errors
      setAddressValidationError(null);

      // Get session data to extract structured address components
      const sessionData = getSessionData();
      const structuredAddress = sessionData?.customerData?.structuredAddress;

      // Create customer object with suburb from structured address
      const customer: Customer = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        address: data.address,
        suburb: structuredAddress?.suburb || undefined,
      };

      // Save customer to database
      let savedCustomer;
      try {

        // Pass session ID to link customer activities
        savedCustomer = await databaseService.upsertCustomer(
          customer,
          sessionData?.sessionId
        );

        // Create lead tracking
        if (sessionData) {
          const leadData = await databaseService.createLead({
            customerId: savedCustomer.id!,
            sessionId: sessionData.sessionId,
            utmSource: sessionData.utmData?.source || "",
            utmMedium: sessionData.utmData?.medium || "",
            utmCampaign: sessionData.utmData?.campaign || "",
            utmTerm: sessionData.utmData?.term || "",
            utmContent: sessionData.utmData?.content || "",
            stage: "contact",
            data: {
              bookingOption: data.bookingOption,
              contactFormData: data,
            },
          });

          // Link UTM tracking to lead if UTM data exists
          if (utmData?.utmTrackingId && leadData?.id) {
            try {
              await fetch("/api/utm/link-lead", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  utmTrackingId: utmData.utmTrackingId,
                  leadId: leadData.id,
                }),
              });
            } catch (linkError) {
              console.error("Error linking UTM to lead:", linkError);
            }
          }
        }

        customer.id = savedCustomer.id;
      } catch (dbError) {
        console.error("Database error:", dbError);
        // Continue with local data if database fails
      }

      // Send GA4 event if UTM data exists and we should send it
      if (utmData?.shouldSendGA4 && ga4ClientId) {
        try {
          const ga4Response = await fetch("/api/utm/send-ga4-event", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              utmTrackingId: utmData.utmTrackingId,
              clientId: ga4ClientId,
              leadData: {
                email: customer.email,
                phone: customer.phone,
                firstName: customer.firstName,
                lastName: customer.lastName,
                propertyAddress: customer.address,
              },
            }),
          });

          const ga4Result = await ga4Response.json();
          if (ga4Result.success) {
            console.log("✅ GA4 form_complete event sent successfully");
          } else {
            console.error("❌ GA4 event failed:", ga4Result.error);
          }
        } catch (ga4Error) {
          console.error("GA4 tracking error:", ga4Error);
          // Don't block form submission if GA4 fails
        }
      }

      // Save customer data
      setCustomer(customer);

      // Update session - preserve form data and add customer data with booking option
      updateSessionData({
        customerData: customer,
        formData: data, // Keep form data intact
        bookingData: {
          serviceType: data.bookingOption, // Store booking option in booking data too
        },
        currentStep: "measurement",
      });

      // Track completion
      analytics.trackFormComplete("contact_form", customer);

      // Record journey step completion (reuse sessionData from earlier)
      userJourneyService.recordStep("contact_form_completed", {
        customerId: customer.id,
        customerName: `${customer.firstName} ${customer.lastName}`,
        customerEmail: customer.email,
        bookingOption: data.bookingOption,
        utmSource: sessionData?.utmData?.source,
        utmMedium: sessionData?.utmData?.medium,
        utmCampaign: sessionData?.utmData?.campaign,
      }); // Trigger form completion webhook (once per session)
      // TODO: Enable webhooks when ready for production
      if (process.env.REACT_APP_ENABLE_WEBHOOKS === "true") {
        try {
          const webhookTriggered =
            await formWebhookService.triggerFormCompletion();
          if (webhookTriggered) {
            console.log("✅ Form completion webhook triggered successfully");
          }
        } catch (webhookError) {
          console.error("Form webhook error:", webhookError);
          // Don't block form submission if webhook fails
        }
      } else {
        console.log(
          "🚫 Webhooks disabled - set REACT_APP_ENABLE_WEBHOOKS=true to enable"
        );
      }

      // Navigate to next step
      setCurrentStep("measurement");
      analytics.trackNavigation("contact_form", "measurement");
      navigate("/measurement");
    } catch (error) {
      console.error("Error submitting contact form:", error);
      analytics.trackFormAbandonment("contact_form", "submit_error");

      // Record journey step error
      userJourneyService.recordStep("contact_form_error", {
        error: error instanceof Error ? error.message : "Unknown error",
        timestamp: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
    }
  };

  // Development helper to test form data persistence
  if (process.env.NODE_ENV === "development") {
    (window as any).testFormPersistence = () => {
      const sessionData = getSessionData();
      console.log("Current session data:", sessionData);
      console.log("Form data:", sessionData?.formData);
      console.log("Customer data:", sessionData?.customerData);
      console.log("Booking data:", sessionData?.bookingData);
    };
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Book Your Window Tinting Consultation
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Let's start with your contact details
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-8 overflow-x-auto">
          <div className="flex items-center space-x-2 sm:space-x-4 min-w-fit px-4 sm:px-0">
            <div className="flex flex-col items-center">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                  1
                </div>
                <span className="ml-1 sm:ml-2 text-xs sm:text-sm font-medium text-amber-600 dark:text-amber-400 hidden xs:inline">
                  Contact
                </span>
              </div>
              {/* Mobile caption */}
              <div className="text-xs mt-1 text-amber-600 dark:text-amber-400 xs:hidden">
                Contact
              </div>
            </div>
            <div className="w-4 sm:w-8 h-px bg-gray-300 dark:bg-gray-600" />
            <div className="flex flex-col items-center">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400 rounded-full flex items-center justify-center text-sm font-medium">
                  2
                </div>
                <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400 hidden xs:inline">
                  Details
                </span>
              </div>
              {/* Mobile caption */}
              <div className="text-xs mt-1 text-gray-500 dark:text-gray-400 xs:hidden">
                Details
              </div>
            </div>
            <div className="w-4 sm:w-8 h-px bg-gray-300 dark:bg-gray-600" />
            <div className="flex flex-col items-center">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400 rounded-full flex items-center justify-center text-sm font-medium">
                  3
                </div>
                <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400 hidden xs:inline">
                  Booking
                </span>
              </div>
              {/* Mobile caption */}
              <div className="text-xs mt-1 text-gray-500 dark:text-gray-400 xs:hidden">
                Booking
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              {...register("firstName")}
              label="First Name"
              placeholder="Enter your first name"
              error={errors.firstName?.message}
              icon={<User className="w-4 h-4" />}
              fullWidth
            />
            <Input
              {...register("lastName")}
              label="Last Name"
              placeholder="Enter your last name"
              error={errors.lastName?.message}
              icon={<User className="w-4 h-4" />}
              fullWidth
            />
          </div>

          {/* Email */}
          <Input
            {...register("email")}
            type="email"
            label="Email Address"
            placeholder="Enter your email address"
            error={errors.email?.message}
            icon={<Mail className="w-4 h-4" />}
            fullWidth
          />

          {/* Phone */}
          <Input
            {...register("phone")}
            type="tel"
            label="Phone Number"
            placeholder="Enter your phone number"
            error={errors.phone?.message}
            icon={<Phone className="w-4 h-4" />}
            fullWidth
          />

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Property Address{" "}
              <span className="text-red-500 text-lg font-bold">*</span>
            </label>{" "}
            <AddressSearch
              value={watch("address") || ""}
              onChange={async (address, placeId, coordinates) => {
                setValue("address", address);

                // Track address selection status
                if (placeId) {
                  // Address was selected from Google Places autocomplete
                  setIsAddressSelected(true);
                  setAddressValidationError(null);

                  // Get structured address components from Google Places
                  let structuredAddress = null;

                  try {
                    // Import Google Places service to extract structured address
                    const { googlePlacesService } = await import(
                      "../../services/googlePlacesService"
                    );
                    if (googlePlacesService.isAvailable()) {
                      const placeDetails =
                        await googlePlacesService.getPlaceDetails(placeId);
                      if (placeDetails) {
                        structuredAddress =
                          googlePlacesService.extractAddressComponents(
                            placeDetails
                          );
                        console.log(
                          "🔍 DEBUG: Structured address from Google Places:",
                          structuredAddress
                        );

                        // Validate that it's a VIC address (additional client-side check)
                        if (
                          structuredAddress?.state !== "VIC" &&
                          structuredAddress?.state !== "Victoria"
                        ) {
                          setAddressValidationError(
                            "Please select an address in Victoria (VIC) only"
                          );
                          setIsAddressSelected(false);
                          return;
                        }
                      }
                    }
                  } catch (error) {
                    console.warn("Failed to get structured address:", error);
                  }

                  // Store place ID, coordinates, and structured address for later use
                  const sessionUpdate: any = {
                    customerData: {
                      ...watchedFields,
                      address,
                      placeId,
                      structuredAddress, // Store structured data
                    },
                    currentStep: "contact",
                  };

                  // Include coordinates if available
                  if (coordinates) {
                    sessionUpdate.addressCoordinates = coordinates;
                    console.log(
                      "🗺️ Address coordinates captured:",
                      coordinates
                    );
                  }

                  updateSessionData(sessionUpdate);
                } else {
                  // Address was manually typed (not selected from autocomplete)

                  // Don't show validation errors if we're currently restoring from session
                  if (isRestoringSession) {
                    console.log(
                      "🔄 Skipping validation during session restoration"
                    );
                    return;
                  }

                  // If we have a valid restored session with address already selected, don't invalidate it
                  if (hasRestoredValidSession && isAddressSelected) {
                    console.log(
                      "✅ Keeping valid restored address selection intact"
                    );
                    return;
                  }

                  // Address was manually typed and not from a valid session
                  setIsAddressSelected(false);

                  // Only show validation error if:
                  // 1. User has typed enough characters AND
                  // 2. They haven't just loaded a valid address from session (avoid immediate errors)
                  if (address.length > 10 && !hasRestoredValidSession) {
                    setAddressValidationError(
                      "Please select a valid address from the suggestions"
                    );
                  } else if (hasRestoredValidSession && address.length > 0) {
                    // User is typing after session restoration - give them a few characters before showing error
                    if (address.length > 15) {
                      setAddressValidationError(
                        "Please select a valid address from the suggestions"
                      );
                    }
                  }
                }
              }}
              placeholder="Enter your property address"
              error={addressValidationError || errors.address?.message}
              required
            />
            {/* Additional validation message for address selection */}
            {addressValidationError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                <span className="mr-1">⚠️</span>
                {addressValidationError}
              </p>
            )}
          </div>

          {/* Booking Option - only show selection if multiple options available */}
          {bookingOptionsLoaded && availableBookingOptions.length > 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Please select an option:
              </label>
              <div className="space-y-3">
                {availableBookingOptions.map((option) => (
                  <label
                    key={option.id}
                    className="flex items-center p-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  >
                    <input
                      {...register("bookingOption")}
                      type="radio"
                      value={option.id}
                      className="w-4 h-4 text-amber-500 border-gray-300 focus:ring-amber-500"
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {option.label}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {option.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <Button type="submit" loading={loading} fullWidth size="lg">
            Continue to Details
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
